import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { PageHeader, StatusBadge, EmptyState, Disclaimer } from "@/components/kit";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { CalendarDays, Plus, CreditCard, Send, MessageCircle, Loader2, CalendarClock, CheckCircle2, Video, MapPin, Package, Compass, Star } from "lucide-react";

const MapEmbed = ({ address }) => (
  <iframe title="Mapa da clínica" loading="lazy" data-testid="clinic-map"
    className="w-full h-44 rounded-xl border border-border mt-2"
    src={`https://www.google.com/maps?q=${encodeURIComponent(address)}&output=embed`} />
);

export default function Appointments() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [appts, setAppts] = useState([]);
  const [types, setTypes] = useState([]);
  const [therapist, setTherapist] = useState(null);
  const [noTherapist, setNoTherapist] = useState(false);
  const [mode, setMode] = useState("request");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ type: "", date: "", time: "", duration: 60, value: 0, modality: "", platform: "" });
  const [slots, setSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [reAppt, setReAppt] = useState(null);
  const [reDate, setReDate] = useState("");
  const [reTime, setReTime] = useState("");
  const [reSlots, setReSlots] = useState([]);
  const [reLoading, setReLoading] = useState(false);
  const [myPackages, setMyPackages] = useState([]);
  const [buyingPkg, setBuyingPkg] = useState(null);
  const [usePackageId, setUsePackageId] = useState("__pay__");
  const [reviewOpen, setReviewOpen] = useState(false);
  const [reviewRating, setReviewRating] = useState(5);
  const [reviewComment, setReviewComment] = useState("");

  const load = () => api.get("/appointments").then(({ data }) => setAppts(data));
  const loadPkgs = () => api.get("/my-packages").then(({ data }) => setMyPackages(data)).catch(() => {});
  useEffect(() => {
    load();
    api.get("/my-therapist").then(({ data }) => {
      setTherapist(data);
      api.get(`/appointment-types?therapist_id=${data.id}`).then((r) => setTypes(r.data));
    }).catch(() => setNoTherapist(true));
    api.get("/settings").then(({ data }) => setMode(data.scheduling_mode));
    loadPkgs();
  }, []);

  const pickType = (name) => {
    const t = types.find((t) => t.name === name);
    setForm({ ...form, type: name, duration: t?.duration || 60, value: t?.value || 0 });
  };

  const loadSlots = async (date) => {
    setForm({ ...form, date, time: "" });
    if (!date || !therapist) return;
    setLoadingSlots(true);
    try {
      const { data } = await api.get(`/availability/slots?therapist_id=${therapist.id}&date=${date}`);
      setSlots(data.slots);
    } catch { setSlots([]); } finally { setLoadingSlots(false); }
  };

  const submit = async () => {
    if (!form.type || !form.date || !form.time) { toast.error("Preencha tipo, data e horário"); return; }
    const usingPkg = usePackageId && usePackageId !== "__pay__";
    setSubmitting(true);
    try {
      const payload = { therapist_id: therapist.id, ...form };
      if (usingPkg) payload.package_purchase_id = usePackageId;
      const { data: appt } = await api.post("/appointments", payload);
      if (!usingPkg && mode === "online_payment" && form.value > 0) {
        const { data } = await api.post("/payments/consultation", { appointment_id: appt.id, origin_url: window.location.origin });
        window.location.href = data.checkout_url;
        return;
      }
      toast.success(usingPkg ? "Consulta agendada usando sessão do pacote!" : "Solicitação enviada ao seu hipnoterapeuta");
      setOpen(false); setForm({ type: "", date: "", time: "", duration: 60, value: 0, modality: "", platform: "" }); setSlots([]); setUsePackageId("__pay__"); load(); loadPkgs();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSubmitting(false); }
  };

  const packages = therapist?.values?.packages || [];
  const availablePackages = myPackages.filter((p) => ((p.sessions || 0) - (p.sessions_used || 0)) > 0);
  const buyPackage = async (idx) => {
    setBuyingPkg(idx);
    try {
      const { data } = await api.post("/payments/package", { therapist_id: therapist.id, package_index: idx, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); setBuyingPkg(null); }
  };

  const upcoming = appts.filter((a) => ["requested", "confirmed", "rescheduled"].includes(a.status));
  const history = appts.filter((a) => ["completed", "cancelled"].includes(a.status));

  const openReschedule = (a) => { setReAppt(a); setReDate(""); setReTime(""); setReSlots([]); };
  const loadReSlots = async (date) => {
    setReDate(date); setReTime("");
    if (!date || !therapist) return;
    setReLoading(true);
    try { const { data } = await api.get(`/availability/slots?therapist_id=${therapist.id}&date=${date}`); setReSlots(data.slots); }
    catch { setReSlots([]); } finally { setReLoading(false); }
  };
  const confirmReschedule = async () => {
    if (!reDate || !reTime) { toast.error("Escolha data e horário"); return; }
    try {
      await api.patch(`/appointments/${reAppt.id}/reschedule`, { date: reDate, time: reTime });
      toast.success("Consulta reagendada");
      setReAppt(null); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const confirmPresence = async (id) => {
    await api.patch(`/appointments/${id}/confirm-presence`);
    toast.success("Presença confirmada!");
    load();
  };

  const openReview = () => {
    setReviewRating(5); setReviewComment(""); setReviewOpen(true);
  };
  const submitReview = async () => {
    try {
      await api.post("/reviews", { therapist_id: therapist.id, rating: reviewRating, comment: reviewComment });
      toast.success("Avaliação enviada. Obrigado pelo feedback!");
      setReviewOpen(false);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const ApptCard = ({ a }) => (
    <Card className="p-5" data-testid={`myappt-${a.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap"><span className="font-medium">{a.type}</span><StatusBadge status={a.status} />
            {a.payment_status === "paid" && <span className="text-xs text-accent font-medium">Pago</span>}
            {a.attendance_confirmed && <span className="text-xs text-accent font-medium">· Presença confirmada</span>}</div>
          <p className="text-sm text-muted-foreground mt-1">
            {new Date(a.date + "T00:00").toLocaleDateString("pt-BR")} às {a.time} · {therapist?.name}
          </p>
          {a.modality && <p className="text-xs text-muted-foreground mt-0.5">{a.modality === "online" ? `Online${a.platform ? " · " + a.platform : ""}` : "Presencial"}{a.modality === "presencial" && a.location ? ` · ${a.location}` : ""}</p>}
          <p className="text-sm font-medium mt-1">R$ {a.value?.toFixed(2)}</p>
        </div>
      </div>

      {a.modality === "online" && a.status !== "cancelled" && a.meeting_url && (
        <a href={a.meeting_url} target="_blank" rel="noreferrer">
          <Button size="sm" className="mt-3 rounded-full" data-testid={`join-online-${a.id}`}><Video className="w-4 h-4 mr-1" />Entrar na sala online</Button>
        </a>
      )}
      {a.modality === "presencial" && a.status !== "cancelled" && (a.location || therapist?.profile?.address) && (
        <MapEmbed address={a.location || therapist?.profile?.address} />
      )}

      {a.status !== "cancelled" && a.status !== "completed" && (
        <div className="flex gap-2 mt-4 flex-wrap">
          {!a.attendance_confirmed && <Button size="sm" onClick={() => confirmPresence(a.id)} data-testid={`confirm-presence-${a.id}`}><CheckCircle2 className="w-4 h-4 mr-1" />Confirmar presença</Button>}
          <Button size="sm" variant="outline" onClick={() => openReschedule(a)} data-testid={`reschedule-${a.id}`}><CalendarClock className="w-4 h-4 mr-1" />Reagendar</Button>
          <Button size="sm" variant="outline" className="text-destructive" onClick={async () => { await api.patch(`/appointments/${a.id}/status`, { status: "cancelled" }); load(); }} data-testid={`cancel-${a.id}`}>Cancelar</Button>
          {therapist?.profile?.whatsapp && <a href={`https://wa.me/${therapist.profile.whatsapp}`} target="_blank" rel="noreferrer"><Button size="sm" variant="outline"><MessageCircle className="w-4 h-4 mr-1" />Falar com profissional</Button></a>}
        </div>
      )}
      {a.status === "completed" && (
        <div className="mt-4">
          <Button size="sm" variant="outline" className="rounded-full" onClick={openReview} data-testid={`review-${a.id}`}><Star className="w-4 h-4 mr-1" />Avaliar profissional</Button>
        </div>
      )}
    </Card>
  );

  if (noTherapist) {
    return (
      <div>
        <PageHeader title="Minhas Consultas" subtitle="Agende e acompanhe suas consultas" />
        <Card className="p-10 text-center" data-testid="no-therapist-banner">
          <Compass className="w-10 h-10 text-primary mx-auto mb-3" />
          <h3 className="font-heading text-lg font-medium">Escolha um hipnoterapeuta para começar</h3>
          <p className="text-muted-foreground text-sm mt-1 max-w-md mx-auto">Encontre profissionais por região — inclusive quem atende online — e agende sua primeira consulta.</p>
          <Button className="rounded-full mt-5" onClick={() => nav("/app/encontrar")} data-testid="find-therapist-btn"><Compass className="w-4 h-4 mr-1" />Encontrar profissional</Button>
        </Card>
        <Disclaimer />
      </div>
    );
  }

  return (
    <div>
      <PageHeader title="Minhas Consultas" subtitle="Agende e acompanhe suas consultas"
        action={<Button onClick={() => setOpen(true)} className="rounded-full" data-testid="new-appointment-btn"><Plus className="w-4 h-4 mr-1" />Agendar consulta</Button>} />

      <Tabs defaultValue="upcoming">
        <TabsList>
          <TabsTrigger value="upcoming" data-testid="tab-upcoming">Próximas ({upcoming.length})</TabsTrigger>
          <TabsTrigger value="history" data-testid="tab-history">Histórico ({history.length})</TabsTrigger>
          <TabsTrigger value="packages" data-testid="tab-packages">Pacotes</TabsTrigger>
        </TabsList>
        <TabsContent value="upcoming" className="space-y-3 mt-4">
          {upcoming.length === 0 ? <EmptyState icon={CalendarDays} title="Nenhuma consulta agendada" desc="Agende sua próxima sessão com seu hipnoterapeuta." /> : upcoming.map((a) => <ApptCard key={a.id} a={a} />)}
        </TabsContent>
        <TabsContent value="history" className="space-y-3 mt-4">
          {history.length === 0 ? <EmptyState icon={CalendarDays} title="Sem histórico" /> : history.map((a) => <ApptCard key={a.id} a={a} />)}
        </TabsContent>
        <TabsContent value="packages" className="mt-4 space-y-4">
          {myPackages.length > 0 && (
            <div>
              <h3 className="font-heading font-medium mb-2">Meus pacotes</h3>
              <div className="grid sm:grid-cols-2 gap-3">
                {myPackages.map((p) => (
                  <Card key={p.id} className="p-4" data-testid={`my-pkg-${p.id}`}>
                    <div className="flex items-center gap-2"><Package className="w-4 h-4 text-accent" /><span className="font-medium">{p.name}</span></div>
                    <p className="text-sm text-muted-foreground mt-1">{Math.max(0, (p.sessions || 0) - (p.sessions_used || 0))} de {p.sessions} sessões disponíveis · R$ {Number(p.price).toFixed(2)}</p>
                  </Card>
                ))}
              </div>
            </div>
          )}
          <div>
            <h3 className="font-heading font-medium mb-2">Pacotes disponíveis</h3>
            {packages.length === 0 ? (
              <EmptyState icon={Package} title="Nenhum pacote oferecido" desc="Seu hipnoterapeuta ainda não disponibilizou pacotes de sessões." />
            ) : (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {packages.map((p, i) => (
                  <Card key={i} className="p-5 flex flex-col" data-testid={`pkg-offer-${i}`}>
                    <div className="flex items-center gap-2"><Package className="w-5 h-5 text-primary" /><span className="font-medium">{p.name}</span></div>
                    {p.sessions ? <p className="text-sm text-muted-foreground mt-1">{p.sessions} sessões</p> : null}
                    <p className="font-heading text-2xl font-semibold text-primary mt-2 flex-1">R$ {Number(p.price).toFixed(2)}</p>
                    <Button className="rounded-full mt-3" onClick={() => buyPackage(i)} disabled={buyingPkg === i} data-testid={`buy-pkg-${i}`}>
                      {buyingPkg === i ? <Loader2 className="w-4 h-4 animate-spin" /> : <><CreditCard className="w-4 h-4 mr-1" />Comprar pacote</>}
                    </Button>
                  </Card>
                ))}
              </div>
            )}
          </div>
        </TabsContent>
      </Tabs>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Agendar consulta com {therapist?.name}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Tipo de consulta</Label>
              <Select value={form.type} onValueChange={pickType}>
                <SelectTrigger className="mt-1" data-testid="appt-type-select"><SelectValue placeholder="Selecione" /></SelectTrigger>
                <SelectContent>{types.map((t) => <SelectItem key={t.id} value={t.name}>{t.name} — R$ {t.value.toFixed(2)}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Data</Label><Input type="date" value={form.date} onChange={(e) => loadSlots(e.target.value)} data-testid="appt-date-input" className="mt-1" /></div>
            <div>
              <Label>Horário disponível</Label>
              {loadingSlots ? <div className="py-3 text-sm text-muted-foreground flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" />Buscando horários...</div> :
                !form.date ? <p className="text-sm text-muted-foreground mt-1">Selecione uma data primeiro.</p> :
                slots.length === 0 ? <p className="text-sm text-muted-foreground mt-1">Nenhum horário disponível nesta data.</p> : (
                <div className="flex flex-wrap gap-2 mt-2">
                  {slots.map((s) => (
                    <button key={s} type="button" onClick={() => setForm({ ...form, time: s })} data-testid={`slot-${s}`}
                      className={`px-3 py-1.5 rounded-full text-sm border ${form.time === s ? "bg-primary text-white border-primary" : "border-border hover:border-primary/40"}`}>{s}</button>
                  ))}
                </div>
              )}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Modalidade</Label>
                <Select value={form.modality} onValueChange={(v) => setForm({ ...form, modality: v })}>
                  <SelectTrigger className="mt-1" data-testid="appt-modality-select"><SelectValue placeholder="Selecione" /></SelectTrigger>
                  <SelectContent><SelectItem value="online">Online</SelectItem><SelectItem value="presencial">Presencial</SelectItem></SelectContent>
                </Select>
              </div>
              {form.modality === "online" && (
                <div><Label>Plataforma</Label>
                  <Select value={form.platform} onValueChange={(v) => setForm({ ...form, platform: v })}>
                    <SelectTrigger className="mt-1" data-testid="appt-platform-select"><SelectValue placeholder="Selecione" /></SelectTrigger>
                    <SelectContent><SelectItem value="Jitsi Meet">Jitsi Meet (link automático)</SelectItem><SelectItem value="Google Meet">Google Meet</SelectItem><SelectItem value="Zoom">Zoom</SelectItem><SelectItem value="Microsoft Teams">Microsoft Teams</SelectItem></SelectContent>
                  </Select>
                </div>
              )}
            </div>
            {form.modality === "online" && (
              <div className="rounded-xl bg-secondary/50 p-3 text-sm flex items-start gap-2"><Video className="w-4 h-4 mt-0.5 text-primary shrink-0" /><span className="text-muted-foreground">Um link de sala exclusivo será gerado automaticamente para esta consulta. Seu profissional pode substituí-lo por um link próprio.</span></div>
            )}
            {form.modality === "presencial" && therapist?.profile?.address && (
              <div>
                <div className="rounded-xl bg-secondary/50 p-3 text-sm flex items-start gap-2"><MapPin className="w-4 h-4 mt-0.5 text-primary shrink-0" /><span><span className="text-muted-foreground">Endereço: </span>{therapist.profile.address}</span></div>
                <MapEmbed address={therapist.profile.address} />
              </div>
            )}
            {availablePackages.length > 0 && (
              <div className="rounded-xl border border-accent/30 bg-accent/5 p-3">
                <Label className="flex items-center gap-1.5"><Package className="w-4 h-4 text-accent" />Usar sessão de um pacote</Label>
                <Select value={usePackageId} onValueChange={setUsePackageId}>
                  <SelectTrigger className="mt-1.5" data-testid="use-package-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__pay__">Pagar normalmente</SelectItem>
                    {availablePackages.map((p) => <SelectItem key={p.id} value={p.id}>{p.name} — {(p.sessions || 0) - (p.sessions_used || 0)} sessões restantes</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            {form.value > 0 && (usePackageId === "__pay__"
              ? <div className="rounded-xl bg-secondary/50 p-3 text-sm flex justify-between"><span className="text-muted-foreground">Valor</span><span className="font-medium">R$ {form.value.toFixed(2)}</span></div>
              : <div className="rounded-xl bg-accent/10 p-3 text-sm flex items-center gap-2"><Package className="w-4 h-4 text-accent" /><span className="text-accent font-medium">Coberto pela sua sessão do pacote — sem cobrança.</span></div>)}
            <p className="text-xs text-muted-foreground">
              {usePackageId !== "__pay__" ? "Uma sessão do seu pacote será utilizada e a consulta ficará confirmada." :
                mode === "online_payment" ? "Você será direcionado ao pagamento (cartão ou PIX). Após o pagamento, sua consulta será confirmada." : "Sua solicitação será enviada ao hipnoterapeuta, que confirmará manualmente."}
            </p>
          </div>
          <DialogFooter>
            <Button onClick={submit} className="rounded-full" disabled={submitting} data-testid="confirm-appt-btn">
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> :
                usePackageId !== "__pay__" ? <><Package className="w-4 h-4 mr-1" />Agendar com pacote</> :
                mode === "online_payment" ? <><CreditCard className="w-4 h-4 mr-1" />Agendar e pagar</> : <><Send className="w-4 h-4 mr-1" />Solicitar agendamento</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!reAppt} onOpenChange={(o) => !o && setReAppt(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Reagendar consulta</DialogTitle></DialogHeader>
          {reAppt && (
            <div className="space-y-3">
              <p className="text-sm text-muted-foreground">{reAppt.type} · atualmente {new Date(reAppt.date + "T00:00").toLocaleDateString("pt-BR")} às {reAppt.time}</p>
              <div><Label>Nova data</Label><Input type="date" value={reDate} onChange={(e) => loadReSlots(e.target.value)} data-testid="re-date-input" className="mt-1" /></div>
              <div>
                <Label>Novo horário</Label>
                {reLoading ? <div className="py-3 text-sm text-muted-foreground flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" />Buscando horários...</div> :
                  !reDate ? <p className="text-sm text-muted-foreground mt-1">Selecione uma data primeiro.</p> :
                  reSlots.length === 0 ? <p className="text-sm text-muted-foreground mt-1">Nenhum horário disponível nesta data.</p> : (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {reSlots.map((s) => (
                      <button key={s} type="button" onClick={() => setReTime(s)} data-testid={`re-slot-${s}`}
                        className={`px-3 py-1.5 rounded-full text-sm border ${reTime === s ? "bg-primary text-white border-primary" : "border-border hover:border-primary/40"}`}>{s}</button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
          <DialogFooter><Button onClick={confirmReschedule} className="rounded-full" data-testid="confirm-reschedule-btn"><CalendarClock className="w-4 h-4 mr-1" />Confirmar novo horário</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={reviewOpen} onOpenChange={setReviewOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Avaliar {therapist?.name}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="flex items-center gap-1">
              {[1, 2, 3, 4, 5].map((n) => (
                <button key={n} type="button" onClick={() => setReviewRating(n)} data-testid={`star-${n}`} className="p-1">
                  <Star className={`w-7 h-7 ${n <= reviewRating ? "fill-amber-400 text-amber-400" : "text-slate-300 dark:text-slate-600"}`} />
                </button>
              ))}
            </div>
            <Textarea value={reviewComment} onChange={(e) => setReviewComment(e.target.value)} placeholder="Conte como foi seu atendimento (opcional)" data-testid="review-comment" rows={4} />
          </div>
          <DialogFooter><Button onClick={submitReview} className="rounded-full" data-testid="submit-review-btn"><Star className="w-4 h-4 mr-1" />Enviar avaliação</Button></DialogFooter>
        </DialogContent>
      </Dialog>
      <Disclaimer />
    </div>
  );
}
